import type { CityConfig } from "../city-template/types";

export const annapolisConfig: CityConfig = {
  city: "Annapolis",
  state: "Maryland",
  stateAbbr: "MD",
  slug: "annapolis",
  route: "/cities/annapolis",
  title: "Emergency Passport Annapolis MD | Expedited Passport Naval Academy | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Annapolis, Maryland — as fast as 24 hours. Fast passport renewal for Annapolis, Severna Park, Edgewater, and Arnold residents. US Naval Academy and sailing community specialists. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Annapolis, MD",
  heroSubtitle: "Annapolis travelers — from the Naval Academy to the sailing community to the historic capital — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving Anne Arundel County.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20panoramic%20view%20of%20Annapolis%20Maryland%20harbor%20with%20historic%20sailboats%20and%20yachts%20docked%20along%20the%20waterfront%2C%20United%20States%20Naval%20Academy%20domes%20visible%20in%20the%20background%2C%20charming%20colonial%20architecture%20along%20Main%20Street%2C%20golden%20sunset%20light%20reflecting%20on%20the%20Chesapeake%20Bay%2C%20warm%20amber%20and%20teal%20color%20palette%2C%20professional%20travel%20photography%2C%20no%20visible%20text%20or%20people%2C%20serene%20maritime%20atmosphere&width=1800&height=700&seq=annapolis-hero-001&orientation=landscape",
  heroImageAlt: "Panoramic view of Annapolis harbor with sailboats, Naval Academy, and colonial architecture at sunset",
  geoPosition: "38.9784;-76.4922",
  geoPlacename: "Annapolis, Maryland",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Washington DC Passport Agency",
    address: "600 19th St NW, 1st Floor, Washington, DC 20006",
    distance: "Approximately 35–45 minutes from Annapolis via US-50 W",
  },
  nearbyCitiesZips: [
    { name: "Annapolis", zips: "21401, 21402, 21403, 21404, 21405, 21409, 21411, 21412" },
    { name: "Severna Park", zips: "21146" },
    { name: "Edgewater", zips: "21037" },
    { name: "Arnold", zips: "21012" },
    { name: "Crofton", zips: "21114" },
    { name: "Gambrills", zips: "21054" },
    { name: "Odenton", zips: "21113" },
    { name: "Pasadena", zips: "21122, 21123" },
    { name: "Glen Burnie", zips: "21060, 21061, 21062" },
    { name: "Crownsville", zips: "21032" },
    { name: "Millersville", zips: "21108" },
    { name: "Riva", zips: "21140" },
    { name: "Davidsonville", zips: "21035" },
    { name: "Harwood", zips: "20776" },
    { name: "Deale", zips: "20751" },
    { name: "Shady Side", zips: "20764" },
    { name: "Lothian", zips: "20711" },
    { name: "Churchton", zips: "20733" },
    { name: "West River", zips: "20778" },
    { name: "Galesville", zips: "20765" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for Annapolis and Chesapeake Bay departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-sailboat-line", title: "Sailing & Naval Academy", desc: "Serving sailors, midshipmen, and maritime professionals" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to downtown DC" },
  ],
  internalLinks: [
    { name: "Expedited passport in Baltimore, MD", route: "/cities/baltimore" },
    { name: "Emergency passport help in Silver Spring, MD", route: "/cities/silver-spring" },
    { name: "Urgent passport services in Columbia, MD", route: "/cities/columbia" },
    { name: "Maryland passport services overview", route: "/states/maryland" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Annapolis, Maryland remotely via secure registered courier. We do not have a walk-in office in Annapolis — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Annapolis and Anne Arundel County travelers without the long drive to downtown DC.",
  fpcOfficeDistance: "Serving Annapolis, Severna Park, Edgewater, Arnold, and all of Anne Arundel County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Anne Arundel County and the Chesapeake Bay region. From the historic streets of downtown Annapolis to the Naval Academy grounds, from the waterfront communities of Severna Park and Edgewater to the sailing marinas of the West River — we serve all of Annapolis's maritime and military communities.",
  nearbyCitiesIntro: "We serve Annapolis and nearby communities including Severna Park, Edgewater, Arnold, Crofton, Gambrills, Odenton, Pasadena, Glen Burnie, Crownsville, Davidsonville, and the southern Anne Arundel County waterfront communities. We also serve Eastern Shore residents who prefer our expedited service over the self-service route.",
  howItWorksIntro: "Most Annapolis travelers complete everything online and ship documents overnight. No need to drive 35–45 minutes to downtown DC or navigate the Chesapeake Bay Bridge traffic.",
  mistakesIntro: "Annapolis is a unique mix of military, maritime, and historic community with significant sailing and international travel. Here are the most common passport mistakes we see from Annapolis travelers.",
  testimonialsIntro: "Annapolis travelers — from Naval Academy families to sailors to waterfront residents — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Annapolis, MD? Here are the most common ones we hear from the Chesapeake Bay and Naval Academy community.",
  statePageRoute: "/states/maryland",
  statePageName: "Maryland",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 35–45 min to downtown DC", "Full day commitment — challenging for military families"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for military and sailing travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Annapolis travelers with immediate departure needs — as fast as 24 hours. Perfect for midshipmen, sailors, and last-minute Chesapeake Bay cruises.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Military and maritime travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Annapolis adults with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to Annapolis address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Annapolis residents, Naval Academy students, and sailing enthusiasts.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Annapolis residents who lost their passport before travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Annapolis and Naval Academy families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Annapolis residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Annapolis MD", description: "Urgent passport processing for Annapolis Maryland travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Annapolis MD", description: "Fast passport renewal for Annapolis Maryland residents with a previously-issued passport." },
    { name: "First-Time Passport Annapolis MD", description: "New passport application services for Annapolis Maryland residents applying for their first passport." },
    { name: "Child Passport Annapolis MD", description: "Minor passport services for Annapolis Maryland families with children under 16." },
  ],
  testimonials: [
    {
      name: "Midshipman J. T.",
      location: "US Naval Academy, Annapolis",
      rating: 5,
      text: "Had a summer training assignment in Japan with 10 days notice. Fast Passport Center handled my first-time passport from start to finish. The agent understood the military timeline and delivered exactly on schedule.",
      service: "First-Time Passport",
    },
    {
      name: "Sarah M.",
      location: "Severna Park, MD",
      rating: 5,
      text: "Our family sailing trip to the Caribbean was in 9 days and my daughter's passport had expired. Fast Passport Center processed her renewal in 4 days. We made our charter departure with time to spare.",
      service: "Child Passport Renewal",
    },
    {
      name: "Captain Robert L.",
      location: "Annapolis Harbor",
      rating: 5,
      text: "As a charter boat captain, I need my passport current for international waters. Fast Passport Center renewed my passport in 5 days. Their maritime knowledge was a huge help.",
      service: "Passport Renewal",
    },
    {
      name: "Karen W.",
      location: "Edgewater, MD",
      rating: 5,
      text: "Lost my passport two weeks before my daughter's wedding in Ireland. I was devastated. Fast Passport Center had a replacement in my hands in 48 hours. Saved the most important trip of my life.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "annapolis-1",
      name: "Annapolis Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1600 W St",
      city: "Annapolis",
      state: "MD",
      zip: "21401",
      phone: "(410) 263-7200",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Annapolis, near the historic district",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Annapolis and Anne Arundel County. Appointments required — book 1–2 weeks in advance. Limited parking during downtown events.",
      icon: "ri-mail-line",
    },
    {
      id: "annapolis-2",
      name: "Severna Park Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "523 Benfield Rd",
      city: "Severna Park",
      state: "MD",
      zip: "21146",
      phone: "(410) 544-0894",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Severna Park, approximately 10 miles north of Annapolis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Severna Park and northern Anne Arundel County residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "annapolis-3",
      name: "Glen Burnie Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "60 W Ordnance Rd",
      city: "Glen Burnie",
      state: "MD",
      zip: "21061",
      phone: "(410) 768-4042",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Glen Burnie, approximately 15 miles north of Annapolis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for northern Anne Arundel County and BWI-area residents. Appointments fill quickly during summer travel season.",
      icon: "ri-mail-line",
    },
    {
      id: "annapolis-4",
      name: "Anne Arundel County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "7 Church Cir",
      city: "Annapolis",
      state: "MD",
      zip: "21401",
      phone: "(410) 222-1397",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown Annapolis, next to the historic circle",
      website: "https://www.aacounty.org/",
      websiteLabel: "Anne Arundel County",
      notes: "County-level facility with full passport acceptance. Extended hours compared to post office locations. Convenient for downtown Annapolis residents.",
      icon: "ri-government-line",
    },
    {
      id: "annapolis-5",
      name: "Odenton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1380 Odenton Rd",
      city: "Odenton",
      state: "MD",
      zip: "21113",
      phone: "(410) 674-2000",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Odenton, approximately 12 miles west of Annapolis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serving western Anne Arundel County and Fort Meade area residents. Call ahead to confirm availability.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited DC slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 35–45 min drive to downtown DC", fpc: "No — ship from Annapolis" },
    { feature: "Military Travel Support", agency: "No flexibility for service needs", fpc: "Yes — we understand military timelines" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Annapolis's military and sailing communities often discover passport needs just weeks before deployment or regatta. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local photo services in Annapolis sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Annapolis and Anne Arundel County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Booking the Acceptance Facility Early",
      description: "Annapolis Post Office appointments fill up fast, especially during summer sailing season and Naval Academy graduation. Book 2–3 weeks ahead.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "Annapolis residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home in Annapolis, Severna Park, or Edgewater." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx location near Annapolis or Glen Burnie." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Annapolis address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Annapolis, MD?", a: "Fast Passport Center offers emergency passport processing in Annapolis, Maryland in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Washington DC Passport Agency (35–45 minutes from Annapolis) — without you needing to make the drive or navigate the Chesapeake Bay Bridge traffic." },
    { q: "Does Annapolis have its own passport agency?", a: "No — neither Annapolis nor the state of Maryland has a regional passport agency. The closest facility is the Washington DC Passport Agency, approximately 35–45 minutes from Annapolis via US-50. Fast Passport Center's courier service means Annapolis residents never need to make that drive." },
    { q: "Can I get expedited passport services if I live in Severna Park or Edgewater?", a: "Absolutely. Fast Passport Center serves all of Anne Arundel County including Severna Park, Edgewater, Arnold, Crofton, Gambrills, Odenton, Pasadena, Glen Burnie, and the southern waterfront communities. Our service works the same way — complete your application online, ship your documents, and we handle the rest." },
    { q: "How much does an expedited passport cost in Annapolis, MD?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your departure timeline." },
    { q: "What airports do Annapolis travelers use for international flights?", a: "Most Annapolis residents fly internationally through Baltimore-Washington International Airport (BWI), approximately 25–30 minutes north via MD-2. Washington Dulles (IAD) and Reagan National (DCA) offer additional international connections. Philadelphia International (PHL) is accessible via the Chesapeake Bay Bridge. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can Naval Academy midshipmen get expedited passport services?", a: "Yes. Fast Passport Center understands the unique travel needs of Naval Academy midshipmen and military personnel. Whether you have a summer training assignment, overseas deployment, or international exchange program, we can expedite your passport processing to meet your military timeline." },
    { q: "Where is the closest passport acceptance facility to downtown Annapolis?", a: "The Annapolis Main Post Office at 1600 W St is the primary downtown acceptance facility. The Anne Arundel County Clerk's Office at 7 Church Cir also offers passport acceptance with extended hours. The Severna Park Post Office at 523 Benfield Rd serves northern Anne Arundel County. All require appointments — book 1–2 weeks ahead." },
    { q: "How do I know if I need expedited or routine passport processing?", a: "If your travel is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. Annapolis residents should plan ahead, especially for sailing season and military assignments." },
  ],
  photoLocations: [
    { name: "Walgreens — Annapolis", address: "1901 West St, Annapolis, MD 21401", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Annapolis", address: "2520 Solomons Island Rd, Annapolis, MD 21401", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Severna Park", address: "560 Ritchie Hwy, Severna Park, MD 21146", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — Glen Burnie", address: "7385 Baltimore Annapolis Blvd, Glen Burnie, MD 21061", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "MVA — Annapolis", address: "1601 Ritchie Hwy, Arnold, MD 21012", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Annapolis and central Anne Arundel County. REAL ID processing available.", featured: true },
    { name: "MVA — Glen Burnie", address: "6601 Ritchie Hwy, Glen Burnie, MD 21062", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Flagship MVA location for the Baltimore-Annapolis corridor. Full service including REAL ID.", featured: false },
  ],
  airports: [
    {
      code: "BWI",
      name: "Baltimore-Washington International Thurgood Marshall",
      distance: "25–30 minutes from Annapolis",
      address: "7050 Friendship Rd, Baltimore, MD 21240",
      note: "Primary international departure airport for Annapolis travelers. Direct flights to London, Cancun, Reykjavik, and the Caribbean. Major domestic hub with Southwest Airlines.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Flight Info",
      featured: true,
    },
    {
      code: "DCA",
      name: "Reagan National (Washington DC)",
      distance: "40–50 minutes from Annapolis",
      address: "2401 Smith Blvd, Arlington, VA 22202",
      note: "Secondary option for international connections via domestic hubs. Convenient for government and military travelers.",
      flightUrl: "https://www.flyreagan.com/",
      flightLabel: "DCA Flight Info",
      featured: false,
    },
    {
      code: "IAD",
      name: "Dulles International (Washington DC)",
      distance: "60–70 minutes from Annapolis",
      address: "1 Saarinen Cir, Dulles, VA 20166",
      note: "Alternative for long-haul international flights to Europe and Asia. United Airlines hub.",
      flightUrl: "https://www.flydulles.com/",
      flightLabel: "IAD Flight Info",
      featured: false,
    },
  ],
};